import {
  MANUAL_UPLOAD_SOURCE_PLATFORM,
  Marketplace,
  TransactionStatus,
  TransactionType,
} from '@commenda/api/new-sales-tax/transactions/types';
import { TransactionSourcePlatformText } from '@commenda/api/sales-tax/constants';
import {
  MarketplaceNameMapping,
  TransactionStatusText,
} from '@commenda/api/sales-tax/types';
import { getAllCountryOptions } from '@commenda/utils/getCountryOptions';
import { Country, RootFiIntegration } from '@prisma/client';
import { State } from 'country-state-city';
import { FilterConfig } from '../../common/filters/types';

export const MANUAL_UPLOAD_OPTION = {
  value: MANUAL_UPLOAD_SOURCE_PLATFORM,
  label: 'Manual upload',
};

const US_TERRITORIES = [
  'GU', // Guam
  'VI', // U.S. Virgin Islands
  'MP', // Northern Mariana Islands
  'AS', // American Samoa
  'UM', // U.S. Minor Outlying Islands
  'FM', // Federated States of Micronesia
  'MH', // Marshall Islands
  'PW', // Palau
  // 'DC', // District of Columbia
];

export const getAllStatesOptions = (countries: Country[]) => {
  const states = countries.map((country) => {
    return (
      State.getStatesOfCountry(country)
        .filter((state) => state.isoCode.length === 2)

        // we added country prefix to the state iso code to ensure this can be used later for public apis
        .map((state) => ({
          value: `${country}_${state.isoCode}`,
          label: state.name,
        }))
    );
  });
  return states.flat();
};

export enum SalesTaxFiltersType {
  PERIOD = 'period',
  REPORTING_PERIOD = 'reporting_period',
  TYPE = 'type',
  DESTINATION_COUNTRIES = 'destination_countries',
  DESTINATION_STATES = 'destination_states',
  SOURCE_PLATFORMS = 'source_platforms',
  STATUSES = 'statuses',
  MARKETPLACES = 'marketplaces',
}

export interface SalesTaxDisableFilterMap {
  [SalesTaxFiltersType.PERIOD]?: boolean;
  [SalesTaxFiltersType.DESTINATION_COUNTRIES]?: boolean;
  [SalesTaxFiltersType.DESTINATION_STATES]?: boolean;
  [SalesTaxFiltersType.SOURCE_PLATFORMS]?: boolean;
  [SalesTaxFiltersType.STATUSES]?: boolean;
}

export const salesTaxFiltersConfig: FilterConfig[] = [
  {
    id: SalesTaxFiltersType.PERIOD,
    type: 'DATE' as const,
    label: 'Period',
    placeholder: 'Select period',
    options: [],
  },

  {
    id: SalesTaxFiltersType.TYPE,
    type: 'SINGULAR' as const,
    label: 'Type',
    placeholder: 'Select type',
    options: [
      {
        value: TransactionType.SALE,
        label: 'Sale',
      },
      {
        value: TransactionType.REFUND,
        label: 'Refund',
      },
      {
        value: TransactionType.PURCHASE,
        label: 'Purchase',
      },
      {
        value: TransactionType.PURCHASE_REFUND,
        label: 'Purchase refund',
      },
    ],
  },

  {
    id: SalesTaxFiltersType.DESTINATION_COUNTRIES,
    type: 'MULTIPLE' as const,
    label: 'Country',
    placeholder: 'Select countries',
    options: getAllCountryOptions().map((country) => ({
      value: country.code,
      label: country.name,
    })),
  },

  {
    id: SalesTaxFiltersType.DESTINATION_STATES,
    type: 'MULTIPLE' as const,
    label: 'Jurisdiction',
    placeholder: 'Select states',
    options: [],
  },

  {
    id: SalesTaxFiltersType.SOURCE_PLATFORMS,
    type: 'SINGULAR' as const,
    label: 'Source platform',
    placeholder: 'Select source platform',
    options: [
      MANUAL_UPLOAD_OPTION,
      ...Object.values(RootFiIntegration).map((integration) => ({
        value: integration,
        label:
          integration && TransactionSourcePlatformText[integration]
            ? TransactionSourcePlatformText[integration]
            : integration,
      })),
    ],
  },
  {
    id: SalesTaxFiltersType.STATUSES,
    type: 'MULTIPLE' as const,
    label: 'Status',
    placeholder: 'Select transaction status',
    options: [
      ...Object.values(TransactionStatus).map((status) => ({
        value: status,
        label: TransactionStatusText[status],
      })),
    ],
  },
  {
    id: SalesTaxFiltersType.MARKETPLACES,
    type: 'MULTIPLE' as const,
    label: 'Marketplace',
    placeholder: 'Select marketplace',
    options: Object.values(Marketplace).map((marketplace) => ({
      value: marketplace,
      label: MarketplaceNameMapping[marketplace]
        ? MarketplaceNameMapping[marketplace]
        : marketplace,
    })),
  },
];
